import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useQuery } from '@apollo/client';

import { InfoCircleIcon } from '@notino/react-styleguide';
import {
  GetCatalogProductViewQuery,
  GetRichContentAvailableQuery,
  GetRichContentAvailableQueryVariables,
} from '@notino/shared/definitions/types';

import { TabWrapper } from '@components';
import ErrorBoundary from '@components/ErrorBoundary';
import { useLivestreams } from '@components/LivestreamBanner/hooks/useLivestreams';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useApolloClientHeaders } from '@hooks/useApolloClientHeaders';
import { TabContent } from '@sharedComponents/TabSwitcher';
import { assignRefs } from '@utils/assignRefs';
import { useIsDesktop } from '@utils/helpers';

import messages from '../../messages';
import getRichContentAvailableQuery from '../../queries/richContentAvailable.graphql';
import { useNewPdDesignEnabled } from '../hooks/useNewPdDesignEnabled';
import { useOpenBVModal } from '../hooks/useOpenBVModal';
import { TabIds, Tabs } from '../model';
import {
  TitleWrapper,
  TextNormal,
  ShowOnMobile,
  ShowOnDesktop,
  DescriptionTitle,
  CharacteristicsTitle,
  ProductTitleWrapper,
  ReviewSummaryCount,
  CompositionTitle,
} from '../styled';

import { AboutBrand } from './AboutBrand';
import { AboutProduct } from './AboutProduct';
import { Composition } from './Composition';
import { ContentSwitcher } from './ContentSwitcher';
import { Other } from './Other';
import LivestreamBanners from './ProductLivestreams';
import { Reviews } from './Reviews';
import { messages as reviewMessages } from './Reviews/messages';
import { CollectReviewsLink, CollectReviewsWrapper } from './Reviews/styled';
import { useTrackReviewListView } from './Reviews/useTrackReviewListView';
import { RichContentArea } from './RichContentArea/loadable';
import { TRACKING_ATTRIBUTES, TAB_IDENTIFIERS } from './tracking';
import { useHandleReviewDigest } from './useHandleReviewDigest';
import { useResetOpenTabOnProductChange } from './useResetOpenTabOnProductChange';
import { getDefaultTab, getProductFullName } from './utils';

interface IProductTabContentProps {
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
  variant: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number];
  rcKey: string;
  customRcKey: string;
  customVersionKey: string;
  reviewsOnServerIds: string[];
}

export const ProductTabContent = ({
  product,
  variant,
  rcKey,
  customRcKey,
  customVersionKey,
  reviewsOnServerIds,
}: IProductTabContentProps) => {
  const { data: richContent } = useQuery<
    GetRichContentAvailableQuery,
    GetRichContentAvailableQueryVariables
  >(getRichContentAvailableQuery, {
    variables: {
      rcKey: customRcKey || rcKey,
      versionKey: customVersionKey,
    },
    skip: !customRcKey && !rcKey,
    ssr: false,
  });

  const { bazaarvoiceIntegrationPd, livestreamOnPdEnabled } = useFeatureFlags();

  const { formatMessage } = useIntl();

  const bvReviewsContainer = React.useRef<HTMLDivElement | null>(null);
  const isFranceShop = useApolloClientHeaders().shopId === 'notino.fr';
  const newPdDesignEnabled = useNewPdDesignEnabled();

  const isMobile = !useIsDesktop({ isNewPdDesign: newPdDesignEnabled });

  const { data, error } = useLivestreams({
    enabled: livestreamOnPdEnabled && isMobile,
    productCode: variant.productCode,
  });

  const { reviewWrapperRef } = useTrackReviewListView();

  useOpenBVModal(bvReviewsContainer);

  useResetOpenTabOnProductChange(product.webMasterId);

  useHandleReviewDigest({ masterId: product.webMasterId });

  /**
   * Characteristics title can be rendered by DescriptionTitle if no description is provided.
   * Otherwise, it is rendered by CharacteristicsTitle on the desktop or inside AboutProduct on mobile.
   */
  const shouldCharsTitleBeRendered =
    Boolean(product.description) && variant.characteristics.length > 0;

  const shouldRenderAboutBrand = Boolean(product.brand?.description);

  const shouldRenderDescriptionTab =
    Boolean(product.description) ||
    Boolean(variant.characteristics && variant.characteristics.length > 0);

  const shouldRenderLivestreamsTab = Boolean(
    livestreamOnPdEnabled &&
      isMobile &&
      !error &&
      data?.getLivestreams?.shows &&
      data?.getLivestreams?.shows.length !== 0
  );

  const documentation = variant.documentation;
  const shouldRenderOthersTab = Boolean(
    documentation?.specialStorage ||
      documentation?.manufacturerImporter ||
      documentation?.importantNotice ||
      isFranceShop
  );

  const shouldRenderComposition =
    Boolean(documentation?.ingredients) ||
    Boolean(documentation?.foodComposition);

  const defaultTab = getDefaultTab({
    shouldRenderDescriptionTab,
    shouldRenderLivestreamsTab,
  });

  const productFullName = getProductFullName(product);
  const productReviewCount = product?.reviewOverview?.textReviewsCount ?? 0;
  const hasPictograms = Boolean(variant.attributes?.Pictograms?.length > 0);

  return (
    <>
      <ErrorBoundary>
        <ContentSwitcher
          product={product}
          variant={variant}
          isRichContentAvailable={
            richContent?.RichContentAvailable?.available ?? false
          }
          defaultTab={defaultTab}
          productReviewCount={productReviewCount}
          shouldRenderComposition={shouldRenderComposition}
          shouldRenderOthersTab={shouldRenderOthersTab}
        />
      </ErrorBoundary>

      <TabWrapper newPdDesignEnabled={newPdDesignEnabled}>
        <div id="tabAnchor">
          {shouldRenderDescriptionTab && (
            <TabContent
              tabId={TabIds.Description}
              trackingLabel={TRACKING_ATTRIBUTES.description}
              tabDividerMobile={true}
              tabIdentifier={TAB_IDENTIFIERS.description}
              isDefaultTab={defaultTab === Tabs.Description}
              isDefaultTabOpen={hasPictograms}
              title={
                <ProductTitleWrapper newPdDesignEnabled={newPdDesignEnabled}>
                  <DescriptionTitle
                    fullWidth={!shouldCharsTitleBeRendered}
                    newPdDesignEnabled={newPdDesignEnabled}
                  >
                    <FormattedMessage
                      {...messages[
                        `${
                          product.description
                            ? 'prodDescription'
                            : `composition${
                                product.isPerfume ? 'Perfume' : 'Cosmetics'
                              }`
                        }`
                      ]}
                      values={{
                        name: (
                          <TextNormal newPdDesignEnabled={newPdDesignEnabled}>
                            {productFullName}
                          </TextNormal>
                        ),
                      }}
                    />
                  </DescriptionTitle>

                  {shouldCharsTitleBeRendered && !newPdDesignEnabled && (
                    <CharacteristicsTitle>
                      <FormattedMessage
                        {...messages[
                          `composition${
                            product.isPerfume ? 'Perfume' : 'Cosmetics'
                          }`
                        ]}
                      />
                    </CharacteristicsTitle>
                  )}
                </ProductTitleWrapper>
              }
            >
              <ErrorBoundary>
                <AboutProduct
                  description={
                    variant.descriptionOverride || product.description
                  }
                  pictograms={variant.attributes?.Pictograms}
                  characteristics={variant.characteristics}
                  composition={product.features.composition || []}
                  isPerfume={product.isPerfume}
                  documentation={variant.documentation}
                  isForSensitiveHair={variant.attributes?.IsForSensitiveHair}
                />
              </ErrorBoundary>
            </TabContent>
          )}
          {shouldRenderComposition && (
            <TabContent
              tabDividerMobile={false}
              tabId={TabIds.Composition}
              trackingLabel={TRACKING_ATTRIBUTES.composition}
              tabIdentifier={TAB_IDENTIFIERS.composition}
              title={
                <TitleWrapper newPdDesignEnabled={newPdDesignEnabled}>
                  <CompositionTitle>
                    <FormattedMessage {...messages.compositionTab} />{' '}
                    <TextNormal newPdDesignEnabled={newPdDesignEnabled}>
                      {productFullName}
                    </TextNormal>
                  </CompositionTitle>
                </TitleWrapper>
              }
            >
              <ErrorBoundary>
                <Composition
                  html={`${variant.documentation.ingredients || ''}
                    ${variant.documentation.foodComposition || ''}`}
                  tablesData={variant.documentation.composition}
                />
              </ErrorBoundary>
            </TabContent>
          )}

          {shouldRenderOthersTab && (
            <TabContent
              tabId={TabIds.Other}
              trackingLabel={TRACKING_ATTRIBUTES.other}
              tabIdentifier={TAB_IDENTIFIERS.other}
              tabDividerMobile={false}
              title={
                <TitleWrapper newPdDesignEnabled={newPdDesignEnabled}>
                  <FormattedMessage {...messages.other} />
                </TitleWrapper>
              }
            >
              <ErrorBoundary>
                <Other
                  documentation={variant.documentation}
                  isFranceShop={isFranceShop}
                />
              </ErrorBoundary>
            </TabContent>
          )}

          {shouldRenderAboutBrand && (
            <TabContent
              tabId={TabIds.AboutBrand}
              trackingLabel={TRACKING_ATTRIBUTES.brand}
              tabIdentifier={TAB_IDENTIFIERS.brand}
              isDefaultTab={defaultTab === Tabs.AboutBrand}
              tabDividerMobile={false}
              title={
                <TitleWrapper newPdDesignEnabled={newPdDesignEnabled}>
                  <FormattedMessage
                    {...messages.aboutBrand}
                    values={{
                      brandName: (
                        <TextNormal newPdDesignEnabled={newPdDesignEnabled}>
                          {product.brand.name}
                        </TextNormal>
                      ),
                    }}
                  />
                </TitleWrapper>
              }
            >
              <ErrorBoundary>
                <AboutBrand description={product.brand.description} />
              </ErrorBoundary>
            </TabContent>
          )}

          {richContent?.RichContentAvailable?.available && (
            <TabContent
              lazyLoad={true}
              tabIdentifier={TAB_IDENTIFIERS.richContent}
              trackingLabel={TRACKING_ATTRIBUTES.richContent}
              tabDividerMobile={false}
              tabId={TabIds.RichContent}
              title={
                <TitleWrapper newPdDesignEnabled={newPdDesignEnabled}>
                  <ShowOnDesktop>
                    <FormattedMessage
                      {...messages.rcHeader}
                      values={{
                        name: customRcKey || productFullName,
                      }}
                    />
                  </ShowOnDesktop>
                  <ShowOnMobile>
                    <FormattedMessage {...messages.fixedHeaderRichContent} />
                  </ShowOnMobile>
                </TitleWrapper>
              }
            >
              <ErrorBoundary>
                <RichContentArea
                  rcKey={rcKey}
                  versionKey={customVersionKey}
                  name={customRcKey || productFullName}
                />
              </ErrorBoundary>
            </TabContent>
          )}

          <TabContent
            tabId={TabIds.Reviews}
            tabDividerMobile={false}
            tabIdentifier={TAB_IDENTIFIERS.reviews}
            trackingLabel={TRACKING_ATTRIBUTES.reviews}
            titleHiddenOnDesktop={!!bazaarvoiceIntegrationPd}
            title={
              <TitleWrapper
                newPdDesignEnabled={newPdDesignEnabled}
                id="pdReviewsScroll"
              >
                <FormattedMessage
                  {...messages.reviewHeader}
                  values={{
                    name: (
                      <>
                        <TextNormal newPdDesignEnabled={newPdDesignEnabled}>
                          {productFullName}
                        </TextNormal>

                        <ReviewSummaryCount
                          id="pdReviewsScroll_reviewCount"
                          newPdDesignEnabled={newPdDesignEnabled}
                        >
                          {productReviewCount > 0 && !bazaarvoiceIntegrationPd
                            ? productReviewCount
                            : ''}
                        </ReviewSummaryCount>
                      </>
                    ),
                  }}
                />
              </TitleWrapper>
            }
          >
            <ErrorBoundary>
              <CollectReviewsWrapper
                bazaarvoiceIntegrationPd={Boolean(bazaarvoiceIntegrationPd)}
              >
                <InfoCircleIcon
                  color="icon.disabled"
                  viewBox="0 0 64 64"
                  height="0.9em"
                  width="24px"
                />

                <CollectReviewsLink
                  href={formatMessage(reviewMessages.collectingReviewsLink)}
                >
                  {formatMessage(reviewMessages.collectingReviewsText)}
                </CollectReviewsLink>
              </CollectReviewsWrapper>

              {bazaarvoiceIntegrationPd ? (
                <div
                  ref={assignRefs(bvReviewsContainer, reviewWrapperRef)}
                  data-bv-show="reviews"
                  data-bv-product-id={product.masterProductCode}
                />
              ) : (
                <Reviews
                  wrapperRef={reviewWrapperRef}
                  reviewsTabId={TabIds.Reviews}
                  renderReviewsOnServer={reviewsOnServerIds?.includes(
                    product.webMasterId
                  )}
                  productCode={product.masterProductCode}
                  masterId={product.webMasterId}
                  variant={variant}
                />
              )}
            </ErrorBoundary>
          </TabContent>

          {shouldRenderLivestreamsTab && (
            <TabContent
              tabId={TabIds.Livestreams}
              tabDividerMobile={false}
              tabIdentifier={TAB_IDENTIFIERS.livestreamsTab}
              isDefaultTab={defaultTab === Tabs.Livestreams}
              isDefaultTabOpen={shouldRenderLivestreamsTab}
              title={
                <TitleWrapper newPdDesignEnabled={newPdDesignEnabled}>
                  <FormattedMessage {...messages.fixedHeaderLivestreams} />
                </TitleWrapper>
              }
            >
              <ErrorBoundary>
                <LivestreamBanners livestreams={data?.getLivestreams?.shows} />
              </ErrorBoundary>
            </TabContent>
          )}
        </div>
      </TabWrapper>
    </>
  );
};
